import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Result, Tabs, Typography } from "antd";
import {
  ContactsOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import CrmLeadsWorkspace from "./CrmLeadsWorkspace";
import CrmOffersTab from "./CrmOffersTab";
import CreateManualLeadModal from "./CreateManualLeadModal";
import CrmLeadDetailView from "./CrmLeadDetailView";
import { createAgentPublicLeadFromOrder } from "../../../services/publicSiteLeadsApi";
import { fetchMyOperationRequestDetail } from "../../../features/operationRequest/operationRequestsApi";
import { useCrmHubEnabled } from "../../../utils/useCrmHubEnabled";
import { ADMIN_OPS_INBOX_PATH } from "./navigateToCrmOpsDocket";
import "./CrmHubPage.css";

const TAB_LEADS = "leads";
const TAB_OFFERS = "offers";
const TAB_DOCKETS = "dockets";

function resolveActiveTab(raw) {
  const value = String(raw || "").trim().toLowerCase();
  if (value === TAB_OFFERS) return TAB_OFFERS;
  if (value === TAB_DOCKETS || value === "files" || value === "תיקים" || value === "דוקטים") {
    return TAB_DOCKETS;
  }
  return TAB_LEADS;
}

function TabLabel({ icon, children }) {
  return (
    <span className="crm-hub-tab-label">
      {icon}
      <span>{children}</span>
    </span>
  );
}

export default function CrmHubPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const leadIdParam = String(searchParams.get("leadId") || "").trim();
  const orderIdParam = String(searchParams.get("orderId") || "").trim();
  const paneParam = String(searchParams.get("pane") || "").trim().toLowerCase();
  const opsTypeParam = String(searchParams.get("opsType") || "").trim();
  const opsRequestIdParam = String(searchParams.get("opsRequestId") || "").trim();
  const fromOpsInbox = String(searchParams.get("from") || "").trim() === "ops-inbox";
  const activeTab = useMemo(() => resolveActiveTab(tabParam), [tabParam]);
  const [modalOpen, setModalOpen] = useState(false);
  const [listNonce, setListNonce] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const crmHubEnabled = useCrmHubEnabled();
  const resolvingOrderRef = useRef("");
  const resolvingOpsRequestRef = useRef("");

  useEffect(() => {
    if (!crmHubEnabled) return;
    const known =
      tabParam === TAB_LEADS ||
      tabParam === TAB_OFFERS ||
      tabParam === TAB_DOCKETS;
    if (known) return;
    const params = new URLSearchParams(searchParams);
    params.set("tab", TAB_LEADS);
    navigate(
      { pathname: "/p/dashboard/drafts", search: `?${params.toString()}` },
      { replace: true }
    );
  }, [crmHubEnabled, navigate, searchParams, tabParam]);

  useEffect(() => {
    if (!leadIdParam) setSelectedLead(null);
  }, [leadIdParam]);

  const setSearch = useCallback(
    (mutator) => {
      const params = new URLSearchParams(searchParams);
      mutator(params);
      navigate(
        { pathname: "/p/dashboard/drafts", search: `?${params.toString()}` },
        { replace: true }
      );
    },
    [navigate, searchParams]
  );

  /** Deep-link: ?orderId=… → ensure docket lead and open it (Ops chat via pane=ops). */
  useEffect(() => {
    if (!crmHubEnabled || leadIdParam || !orderIdParam) return;
    if (resolvingOrderRef.current === orderIdParam) return;
    resolvingOrderRef.current = orderIdParam;
    let cancelled = false;
    (async () => {
      try {
        const data = await createAgentPublicLeadFromOrder(orderIdParam);
        if (cancelled) return;
        const lead = data?.lead;
        if (!lead?.id) return;
        setSelectedLead(lead);
        setSearch((params) => {
          params.set("tab", TAB_DOCKETS);
          params.set("leadId", String(lead.id));
          params.delete("orderId");
          if (paneParam) params.set("pane", paneParam);
          if (opsTypeParam) params.set("opsType", opsTypeParam);
          if (opsRequestIdParam) params.set("opsRequestId", opsRequestIdParam);
        });
      } catch {
        resolvingOrderRef.current = "";
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    crmHubEnabled,
    leadIdParam,
    orderIdParam,
    opsRequestIdParam,
    opsTypeParam,
    paneParam,
    setSearch,
  ]);

  /**
   * Deep-link: ?opsRequestId=… (general / order-less ops) → ensure CRM docket via
   * agent ops detail (attach crmLeadId) and open Ops chat.
   */
  useEffect(() => {
    if (!crmHubEnabled || leadIdParam || orderIdParam || !opsRequestIdParam) return;
    if (resolvingOpsRequestRef.current === opsRequestIdParam) return;
    resolvingOpsRequestRef.current = opsRequestIdParam;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMyOperationRequestDetail(opsRequestIdParam);
        if (cancelled) return;
        const req = data?.request;
        const leadId = String(req?.crmLeadId || req?.crm_lead_id || "").trim();
        if (!leadId) {
          resolvingOpsRequestRef.current = "";
          return;
        }
        setSelectedLead(
          req
            ? {
                id: leadId,
                linked_booking: {
                  ops_request_id: opsRequestIdParam,
                  request_type:
                    req.requestType || req.request_type || opsTypeParam || null,
                },
              }
            : { id: leadId }
        );
        setSearch((params) => {
          params.set("tab", TAB_DOCKETS);
          params.set("leadId", leadId);
          params.set("pane", "ops");
          params.set("opsRequestId", opsRequestIdParam);
          const type = String(
            opsTypeParam || req?.requestType || req?.request_type || ""
          ).trim();
          if (type) params.set("opsType", type);
        });
      } catch {
        resolvingOpsRequestRef.current = "";
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    crmHubEnabled,
    leadIdParam,
    opsRequestIdParam,
    opsTypeParam,
    orderIdParam,
    setSearch,
  ]);

  const onTabChange = useCallback(
    (nextKey) => {
      const next = resolveActiveTab(nextKey);
      setSearch((params) => {
        params.set("tab", next);
        params.delete("leadId");
      });
    },
    [setSearch]
  );

  const openLead = useCallback(
    (lead, { tab } = {}) => {
      if (!lead?.id) return;
      setSelectedLead(lead);
      const keepTab = resolveActiveTab(tab || activeTab);
      setSearch((params) => {
        params.set("tab", keepTab === TAB_OFFERS ? TAB_LEADS : keepTab);
        params.set("leadId", String(lead.id));
      });
    },
    [activeTab, setSearch]
  );

  const closeLead = useCallback(() => {
    if (fromOpsInbox) {
      navigate(ADMIN_OPS_INBOX_PATH);
      return;
    }
    setSelectedLead(null);
    setSearch((params) => {
      params.delete("leadId");
      params.delete("pane");
      params.delete("opsType");
      params.delete("opsRequestId");
      params.delete("orderId");
      params.delete("from");
      if (!params.get("tab")) params.set("tab", TAB_LEADS);
    });
  }, [fromOpsInbox, navigate, setSearch]);

  const onLeadUpdatedStable = useCallback((nextLead) => {
    setSelectedLead(nextLead);
  }, []);

  const onLeadDeleted = useCallback(() => {
    setListNonce((n) => n + 1);
  }, []);

  const headerExtra = useMemo(
    () => (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)}
        style={{
          borderRadius: 10,
          fontWeight: 600,
          background: "#e85d04",
          borderColor: "#e85d04",
        }}
      >
        ליד חדש
      </Button>
    ),
    []
  );

  const items = useMemo(
    () => [
      {
        key: TAB_LEADS,
        label: (
          <TabLabel icon={<ContactsOutlined aria-hidden />}>לידים</TabLabel>
        ),
        children: (
          <CrmLeadsWorkspace
            key={`leads-${listNonce}`}
            embedded
            variant="leads"
            showCreateOffer
            title="לידים"
            headerExtra={headerExtra}
            onOpenLead={(lead) => openLead(lead, { tab: TAB_LEADS })}
          />
        ),
      },
      {
        key: TAB_OFFERS,
        label: (
          <TabLabel icon={<FileTextOutlined aria-hidden />}>הצעות</TabLabel>
        ),
        children: <CrmOffersTab />,
      },
      {
        key: TAB_DOCKETS,
        label: (
          <TabLabel icon={<FolderOpenOutlined aria-hidden />}>דוקטים</TabLabel>
        ),
        children: (
          <CrmLeadsWorkspace
            key={`dockets-${listNonce}`}
            embedded
            variant="dockets"
            showCreateOffer
            title="דוקטים"
            onOpenLead={(lead) => openLead(lead, { tab: TAB_DOCKETS })}
          />
        ),
      },
    ],
    [headerExtra, listNonce, openLead]
  );

  if (!crmHubEnabled) {
    return (
      <div className="crm-hub-page" dir="rtl">
        <Result
          status="403"
          title="אין הרשאה"
          subTitle="מערכת ה-CRM אינה זמינה עבור החשבון הזה."
          extra={
            <Button type="primary" onClick={() => navigate("/p/dashboard/home")}>
              חזרה לדשבורד
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="crm-hub-page">
      <div className="crm-hub-header">
        <Typography.Title level={3} className="crm-hub-title">
          CRM — לידים, הצעות ודוקטים
        </Typography.Title>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={items}
        destroyOnHidden
        className="crm-hub-tabs"
      />
      <CreateManualLeadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => {
          setModalOpen(false);
          setListNonce((n) => n + 1);
        }}
      />
      {leadIdParam ? (
        <CrmLeadDetailView
          leadId={leadIdParam}
          initialLead={
            selectedLead && String(selectedLead.id) === leadIdParam
              ? selectedLead
              : null
          }
          initialLeftPane={paneParam === "ops" ? "ops" : null}
          initialOpsType={opsTypeParam || null}
          initialOpsRequestId={opsRequestIdParam || null}
          backLabel={fromOpsInbox ? "חזרה לפניות אופרציה" : null}
          showCreateOffer
          onClose={closeLead}
          onDeleted={onLeadDeleted}
          onLeadUpdated={onLeadUpdatedStable}
        />
      ) : null}
    </div>
  );
}
